<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { TableGridItem } from '@/models/table-grid-interface';
import { useDisplay } from 'vuetify';
import RevolverMatrixCard from './RevolverMatrixCard.vue';
import RevolverMatrixMiniCard from './RevolverMatrixMiniCard.vue';

const { xs } = useDisplay();

const props = withDefaults(defineProps<{
  users: (TableGridItem | null)[];
  level: 0 | 1 | 2;
  width: number;
  titleField: string;
  subtitleField: string;
  titleFieldMax: number;
  subtitleFieldMax: number;
  cardBackgroundColor: string;
  emptySlotColor: string;
  backgroundColor: string;
  // New props
  cardClass?: string;
  emptyCardClass?: string;
  cardTitleColor?: string;
  cardSubtitleColor?: string;
  avatarSizeLevel0?: number;
  avatarSizeLevel1?: number;
  avatarSizeLevel2?: number;
  forceMiniCards?: boolean;
  showEmptyIcon?: boolean;
  emptyText?: string;
  arrowColor?: string;
}>(), {
  cardClass: '',
  emptyCardClass: '',
  cardTitleColor: '#000000',
  cardSubtitleColor: '#757575',
  avatarSizeLevel0: 60,
  avatarSizeLevel1: 50,
  avatarSizeLevel2: 40,
  forceMiniCards: false,
  showEmptyIcon: true,
  emptyText: '',
  arrowColor: '#ffffff',
});

const emit = defineEmits<{
  (e: 'cardClick', user: TableGridItem | null): void;
}>();

// Animation state
const showCards = ref(false);

onMounted(() => {
  // Delay based on level for cascading effect
  setTimeout(() => {
    showCards.value = true;
  }, props.level * 150);
});

// Use mini cards on mobile for level 2 or when forced
const useMiniCards = computed(() => {
  if (props.forceMiniCards && props.level === 2) return true;
  return (xs.value) && props.level === 2;
});

function getCardSize(level: number): 'large' | 'medium' | 'small' {
  switch (level) {
    case 0: return 'large';
    case 2: return 'small';
    default: return 'medium';
  }
}

// Get avatar size based on level
const avatarSize = computed(() => {
  switch (props.level) {
    case 0: return props.avatarSizeLevel0;
    case 1: return props.avatarSizeLevel1;
    case 2: return props.avatarSizeLevel2;
    default: return props.avatarSizeLevel1;
  }
});

// Show arrow for all levels except bottom (level 2)
const showArrow = computed(() => props.level < 2);

// Calculate width percentage for each card based on level
const cardWidthPercent = computed(() => {
  if (props.level === 0) return 100;
  if (props.level === 1) return 100 / props.width;
  // Level 2: width^2 cards
  return 100 / (props.width * props.width);
});

// Gap between cards
const gapSize = computed(() => props.level === 2 ? '4px' : '8px');

// Minimum height for card placeholder based on level (not used for mini cards - they use aspect-ratio)
const minHeight = computed(() => {
  if (useMiniCards.value) return 'auto';
  switch (props.level) {
    case 0: return '120px';
    case 2: return '100px';
    default: return '110px';
  }
});

// Get transition name - always slide from top
function getTransition(): string {
  return 'slide-y-transition';
}

// Delay based on level
function getDelay(): string {
  return `${props.level * 50}ms`;
}
</script>

<template>
  <div
    class="d-flex justify-center mb-4"
    :style="{
      width: '100%',
      gap: gapSize
    }"
  >
    <!-- Wrapper div to reserve space -->
    <div
      v-for="(user, index) in users"
      :key="`level-${level}-${index}`"
      class="d-flex justify-center align-center"
      :style="{
        flexBasis: `calc(${cardWidthPercent}% - ${gapSize})`,
        maxWidth: `calc(${cardWidthPercent}% - ${gapSize})`,
        flexShrink: 0,
        minHeight: minHeight
      }"
    >
      <transition
        :name="getTransition()"
        appear
      >
        <!-- Mini card for mobile on level 2 -->
        <RevolverMatrixMiniCard
          v-if="showCards && useMiniCards"
          :user="user"
          :titleField="titleField"
          :subtitleField="subtitleField"
          :titleFieldMax="titleFieldMax"
          :subtitleFieldMax="subtitleFieldMax"
          :cardBackgroundColor="cardBackgroundColor"
          :cardClass="cardClass"
          :emptyCardClass="emptyCardClass"
          :showEmptyIcon="showEmptyIcon"
          :emptyText="emptyText"
          :style="{ transitionDelay: getDelay(), width: '100%' }"
          @click="emit('cardClick', user)"
        />
        <!-- Regular card -->
        <RevolverMatrixCard
          v-else-if="showCards"
          :user="user"
          :size="getCardSize(level)"
          :titleField="titleField"
          :subtitleField="subtitleField"
          :titleFieldMax="titleFieldMax"
          :subtitleFieldMax="subtitleFieldMax"
          :cardBackgroundColor="cardBackgroundColor"
          :emptySlotColor="emptySlotColor"
          :showArrow="showArrow"
          :backgroundColor="backgroundColor"
          :cardClass="cardClass"
          :emptyCardClass="emptyCardClass"
          :cardTitleColor="cardTitleColor"
          :cardSubtitleColor="cardSubtitleColor"
          :avatarSize="avatarSize"
          :showEmptyIcon="showEmptyIcon"
          :emptyText="emptyText"
          :arrowColor="arrowColor"
          :style="{
            transitionDelay: getDelay(),
            width: '100%',
            height: '100%'
          }"
          @click="emit('cardClick', user)"
        />
      </transition>
    </div>
  </div>
</template>
